import { Injectable } from '@angular/core';
import _ from 'lodash';

import { contentSlug } from '../../../../content-slug';
import { Region, SUBREGION_KIND, Suburb } from '../../../../models/content';
import { ContentRegionComponent, RegionPageAction } from '../../content-region.component';
import { displayName } from '../map/suburb-map.engine';
import { ContentSuburbComponent, SuburbPublishAction } from '../page/content-suburb.component';
import {
  PageTemplateEditorComponent,
  TemplateEditorResult,
} from '../template/page-template-editor.component';
import { TEMPLATE_KEYS, TemplateKey } from '../template/suburb-template';
import { SuburbCatalogueService } from './catalogue.service';
import { ContentRecordsService } from './content-records.service';
import { EditorHostService } from './editor-host.service';
import { freeSlot } from './map-colours';
import { PagePublisherService } from './page-publisher.service';
import { Panel } from './panel';
import { SubRegionsService } from './subregions.service';
import { SuburbDetailService } from './suburb-detail.service';
import { WorkspaceStateService } from './workspace-state.service';

interface PageEditorResult {
  saved: boolean;
  suburb: Suburb;
  publishAction: SuburbPublishAction;
  /** A page designed before the template was converted (#262). */
  converted?: boolean;
}

interface RegionEditorResult {
  saved: boolean;
  region: Region;
}

/**
 * The page and region editors in the side panel (#253, #256, #260, #262): a suburb's page (its Page tab), a
 * sub-region's or a place's, a new sub-region or place page, a region, and the Suburb and Sub-region page templates.
 * The editors are the content editors' own components, given the same inputs as their popups; this decides what they
 * open on and where the panel goes when they close.
 */
@Injectable()
export class PageEditorService {
  /** ABS suburb slugs per state (from the suburb graph), for the sub-region editor's address warning. */
  private absSlugs: Map<string, Set<string>> | null = null;

  constructor(
    private editor: EditorHostService,
    private records: ContentRecordsService,
    private publisher: PagePublisherService,
    private catalogue: SuburbCatalogueService,
    private subRegions: SubRegionsService,
    private detail: SuburbDetailService,
    private state: WorkspaceStateService
  ) {}

  /** The Page tab: the selected suburb's page in the editor, or a new one (not saved until Save or Publish). */
  openPageTab(): void {
    const code = this.state.selectedCode;
    if (!code) return;
    const record = this.records.suburbRecord(code);
    const regionId = this.state.selectedArea.regionId;
    if (record) this.editPage(record, false);
    else if (regionId && !this.state.readOnly)
      this.editPage(
        this.records.newSuburb(
          code,
          regionId,
          this.detail.name || this.state.engine.areaName(code) || ''
        ),
        true
      );
  }

  /** "Places and other pages" → Place page: a new place page, unpublished. */
  addPlacePage(): void {
    this.state.panel = { kind: 'page-edit', title: 'New place page' };
    this.editPage(this.records.newPlace(), true);
  }

  editOtherPage(record: Suburb): void {
    this.state.panel = { kind: 'page-edit', title: record.name || 'Page' };
    this.editPage(record, false);
  }

  /** A sub-region's page, from its own panel or from Regions (`back` is where the panel returns). */
  editSubRegionPage(id: string, back: Panel = { kind: 'subregion', id }): void {
    if (!this.editor.leave()) return;
    const record = this.records.recordById(id);
    if (!record) return;
    this.state.panel =
      back.kind === 'subregion' ? back : { kind: 'page-edit', title: `Edit ${record.name}` };
    this.editPage(record, false, back);
  }

  /** A new sub-region in this region: a district record, unpublished, in the next free colour (saved by Save). */
  newSubRegion(regionId: string): void {
    if (!this.editor.leave()) return;
    const seed = this.records.newSubRegion(
      regionId,
      freeSlot(this.catalogue.subRegionsOf(regionId))
    );
    this.state.panel = {
      kind: 'page-edit',
      title: `New sub-region in ${this.catalogue.regionName(regionId)}`,
    };
    this.editPage(seed, true, { kind: 'subregion', id: seed._meta.id });
  }

  /** Add a region, or edit one: the Regions editor in the panel (publishing stays on the Regions card). */
  editRegion(regionId: string | null): void {
    if (!this.editor.leave()) return;
    const region = regionId
      ? _.cloneDeep(this.records.regions().find((r) => r._meta.id === regionId))
      : this.records.newRegion();
    if (!region) return;
    const isNew = !regionId;
    this.state.panel = { kind: 'region-edit', title: isNew ? 'New region' : `Edit ${region.name}` };
    const data = {
      region,
      isNew,
      designedPage: region.slug ? this.records.regionPage(region) : undefined,
      save: (r: { region: Region; pageAction?: RegionPageAction }) =>
        this.records.writeWithPage(r.region, r.pageAction),
    };
    this.editor.mount(ContentRegionComponent, data, (result: RegionEditorResult | undefined) => {
      if (result?.saved) {
        this.records.cache('regions', result.region);
        this.state.setStatus(
          `Saved region ${result.region.name}. Publish it from the Regions card to put it on the website.`
        );
        void this.catalogue.reload();
      }
      this.state.panel = { kind: 'regions' };
    });
  }

  /** A page template's editor in the panel (#262); it goes back to the panel it was opened from. */
  editTemplate(key: TemplateKey): void {
    if (!this.editor.leave()) return;
    const back = this.state.panel;
    const label = TEMPLATE_KEYS.find((t) => t.key === key)?.label ?? key;
    this.state.panel = { kind: 'template', key, title: `${label} template` };
    this.editor.mount(
      PageTemplateEditorComponent,
      { key },
      (r: TemplateEditorResult | undefined) => {
        if (r?.saved)
          this.state.setStatus(templateSavedStatus(label, r), r.notShipped === 'failed');
        this.state.panel = back?.kind === 'template' ? null : back;
      }
    );
  }

  /**
   * Type a page that isn't one suburb (sub-region or place), e.g. brisbane/gold-coast, which has none yet. A page made
   * a sub-region moves to its region in Regions (and onto the map). Written from a freshly read copy.
   */
  setKind(record: Suburb, kind: typeof SUBREGION_KIND | 'place'): void {
    if (!kind || record.kind === kind) return;
    this.records.writeFresh(
      record,
      (fresh) => ({ ...fresh, kind }),
      (next) => {
        const sub = kind === SUBREGION_KIND;
        const regionName = this.catalogue.regionName(next.regionId);
        this.state.setStatus(
          sub
            ? `${next.name} is now a sub-region of ${regionName}: it's listed under its region in Regions.`
            : `${next.name} is now a place.`
        );
        if (sub) void this.catalogue.reload();
      }
    );
  }

  /**
   * The Page tab in the panel (#262: the record's fields and inserted blocks; it saves them itself); `back` is the
   * panel to return to when it closes (else the suburb or the list).
   */
  private editPage(record: Suburb, isNew: boolean, back?: Panel): void {
    const suburb = _.cloneDeep(record);
    const sub = suburb.kind === SUBREGION_KIND;
    if (sub) this.loadAbsSlugs();
    const data = {
      suburb,
      isNew,
      ...(sub
        ? {
            members: this.state.memberCounts.get(suburb._meta.id) ?? 0,
            absSlugs: (state: string) => this.absSlugs?.get(state) ?? null,
          }
        : {}),
    };
    this.editor.mount(ContentSuburbComponent, data, (result: PageEditorResult | undefined) => {
      if (result?.saved) this.publisher.afterSave(result.suburb, result.publishAction ?? null);
      if (result?.converted) {
        this.state.setStatus(
          `Converted the page for ${record.name}: publish it to put the template version on the website.`
        );
        void this.catalogue.reload();
      }
      this.state.panel = this.panelAfterEditing(back, isNew && !result?.saved);
      this.subRegions.reloadIfOpen();
    });
  }

  /** Where the panel goes when the page editor closes. A new sub-region that wasn't saved has no panel: back to Regions. */
  private panelAfterEditing(back: Panel | undefined, unsavedNew: boolean): Panel | null {
    const panel = this.state.panel;
    if (back) return back.kind === 'subregion' && unsavedNew ? { kind: 'regions' } : back;
    if (panel?.kind === 'suburb') return { ...panel, tab: 'overview' };
    if (panel?.kind === 'page-edit') return { kind: 'other-pages' };
    return panel;
  }

  private loadAbsSlugs(): void {
    if (this.absSlugs) return;
    this.state.engine
      .graph()
      .then((g) => {
        const out = new Map<string, Set<string>>();
        for (const [name, st] of Object.values(g.nodes)) {
          let slugs = out.get(st);
          if (!slugs) out.set(st, (slugs = new Set()));
          slugs.add(contentSlug(displayName(name)));
        }
        this.absSlugs = out;
      })
      .catch(() => {}); // without them the editor just doesn't warn about a suburb's address
  }
}

/** The status line after a template's Save or Publish. */
function templateSavedStatus(label: string, r: TemplateEditorResult): string {
  if (r.published)
    return `Saved the ${label} template: every published page built from it is on its way to the website preview.`;
  if (r.notShipped === 'not-set-up')
    return `Saved the ${label} template. Website publishing isn't set up on this Conductor, so it goes out with the next site publish.`;
  if (r.notShipped === 'failed')
    return `Saved the ${label} template, but publishing didn't start. Try Publish again.`;
  return `Saved the ${label} template. Publish it to put it on the website.`;
}
